import axios from 'axios';
import catchify from 'catchify';
import set from 'lodash/set';

import {
  STRIPE_ELEMENT_SLUG,
  STRIPE_ELEMENTS_PAYMENT_OPTIONS,
} from '@/constants/change-plan.js';
import { getCSRFCookie } from '~/util/global';

const buildApiRequest = (
  url,
  method = 'get',
  data = {},
  axiosInstance = axios,
  headers = { 'Content-Type': 'application/json' },
) => (
  catchify(axiosInstance({
    data,
    url,
    method,
    withCredentials: true,
    headers: {
      'X-CSRFToken': getCSRFCookie(),
      ...headers,
    },
  }))
);

const initialState = () => ({
  billingCountry: null,
  billingPostalCode: null,
  billingTaxId: null,
  error: '',
  invoice: null,
  isComplete: false,
  isLoading: false,
  isReady: false,
  paymentType: '',
  taxRate: null,
});

export const mutations = {
  resetState(state) {
    const initState = initialState();
    Object.assign(state, initState);
  },
  setError(state, value) {
    set(state, 'error', value);
  },
  setIsComplete(state, value) {
    set(state, 'isComplete', value);
  },
  setIsLoading(state, value) {
    set(state, 'isLoading', value);
  },
  setIsReady(state, value) {
    set(state, 'isReady', value);
  },
  setPaymentType(state, value) {
    set(state, 'paymentType', value);
  },
  setBillingDetails(state, value) {
    const { billingCountry, billingPostalCode, billingTaxId } = value;

    set(state, 'billingCountry', billingCountry);
    set(state, 'billingPostalCode', billingPostalCode);
    set(state, 'billingTaxId', billingTaxId);
  },
  setInvoice(state, value) {
    set(state, 'invoice', value);
  },
  setTaxRate(state, value) {
    set(state, 'taxRate', value);
  },
};

export const getters = {
  error: (state) => state.error,
  isComplete: (state) => state.isComplete,
  isLoading: (state) => state.isLoading,
  isReady: (state) => state.isReady,
  paymentType: (state) => state.paymentType,
  invoice: (state) => state.invoice,
  billingCountry: (state) => state.billingCountry,
  billingPostalCode: (state) => state.billingPostalCode,
  billingTaxId: (state) => state.billingTaxId,
  billingDetails: (state, stateGetters) => {
    const { billingCountry, billingPostalCode } = stateGetters;
    const detailsObj = {
      address: {
        country: billingCountry,
      },
    };

    if (billingCountry === 'US') {
      detailsObj.address.postal_code = billingPostalCode;
    }

    return detailsObj;
  },
  taxRate: (state) => state.taxRate,
};

export const actions = {
  onChange({ state, commit }, event = {}) {
    const {
      value,
      complete,
      error,
    } = event;

    if (value.type && value.type !== state.paymentType) {
      commit('setPaymentType', value.type);

      const pmObj = {
        key: 'stripe-elements-payment',
        id: STRIPE_ELEMENT_SLUG,
        brand: value.type,
        paymentType: STRIPE_ELEMENT_SLUG,
        label: STRIPE_ELEMENTS_PAYMENT_OPTIONS[value.type]?.label || 'Stripe payment',
      };

      commit('changePlan/setSelectedPaymentMethodObject', pmObj, { root: true });
    }

    commit('setIsComplete', complete);
    commit('setError', error);
  },

  async queryInvoice({ commit }, orderId) {
    if (!orderId) return;

    const [, invoiceData] = await buildApiRequest(
      'cart/stripe-elements/order/status/',
      'post',
      { order_id: orderId },
      this.$axios,
    );

    if (!invoiceData?.data) return;

    if (Object.keys(invoiceData.data).length !== 0) {
      commit('setInvoice', invoiceData.data);
    }
  },

  async retrieveTaxRate({ commit, dispatch }, data) {
    const {
      abn,
      abnBusinessName,
      country,
      postalCode,
      saveAbn,
      saveBillingDetails,
      dealId,
    } = data;

    commit('setIsLoading', true);

    const taxData = {
      country,
      postal_code: postalCode,
      tax_id: abn,
      deal_id: dealId,
    };

    const billingData = {
      tax_business_name: abnBusinessName,
      set_billing_default: saveBillingDetails,
      update_cart: saveBillingDetails,
      set_tax_default: saveAbn,
      tax_id_type: 'au_abn', // Hardcoded for now
      ...taxData,
    };

    const [[taxErr, taxRes], [billErr]] = await Promise.all([
      buildApiRequest(
        '/api/tax_rate/',
        'post',
        taxData,
        this.$axios,
      ),
      buildApiRequest(
        '/api/users/update_billing_info/',
        'post',
        billingData,
        this.$axios,
      ),
    ]);

    const defaultErrMsg = 'Something went wrong updating your billing info.';
    if (taxErr) {
      commit('setError', taxErr.response?.data?.error || defaultErrMsg);
      commit('setIsLoading', false);
      return;
    }
    if (billErr) {
      commit('setError', billErr.response?.data || defaultErrMsg);
      commit('setIsLoading', false);
      return;
    }

    // fetch users cards before setting payments
    await dispatch('changePlan/fetchUserCards', {}, { root: true });

    // Set billing details
    commit('setBillingDetails', {
      billingCountry: country,
      billingPostalCode: postalCode,
      billingTaxId: abn,
    });
    commit('setTaxRate', taxRes.data.tax_rate);
    commit('changePlan/setIsBillingDetailsValid', true, { root: true });
    commit('changePlan/setIsABNValid', true, { root: true });
    commit('setError', null);
    commit('setIsLoading', false);
  },

  resetUpgradeState({ commit }) {
    commit('resetState');
    commit('changePlan/setIsBillingDetailsValid', false, { root: true });
    commit('changePlan/setIsABNValid', false, { root: true });
    commit('changePlan/setNewPlanSelected', null, { root: true });
    commit('changePlan/setCurrentStepKey', 'select-tier', { root: true });
  },
};

export { initialState as state };
